<template>
    <div class="selection-page">
        <v-container fluid class="selection-container pa-4 pa-sm-6 pa-md-8">
            <v-row justify="center">
                <v-col cols="12" sm="10" md="8" lg="8" xl="6">
                    <v-card class="option-list" elevation="3">
                        <v-card-title class="d-flex justify-space-between align-center text-subtitle-1 font-weight-bold">
                            <div class="d-flex align-center">
                                <v-icon small color="primary" class="mr-1">mdi-format-quote-open</v-icon>
                                评选活动
                            </div>
                            <v-btn
                                color="primary"
                                text
                                small
                                @click="dialog = true"
                                :disabled="!isLoggedIn || loading"
                            >
                                <v-icon small class="mr-1">mdi-plus</v-icon>
                                添加选项
                            </v-btn>
                        </v-card-title>
                        <v-card-text class="pa-0">
                            <v-progress-linear
                                v-if="loading"
                                color="primary"
                                indeterminate
                                height="3"
                                class="mb-2"
                            ></v-progress-linear>
                            <v-list v-if="options.length > 0">
                                <v-list-item
                                    v-for="option in options"
                                    :key="option.id"
                                    class="option-item"
                                    :dense="$vuetify.breakpoint.smAndDown"
                                >
                                    <v-list-item-content>
                                        <v-list-item-title class="option-content">
                                            {{ option.content }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                        <v-btn
                                            text
                                            icon
                                            @click="toggleLike(option.id)"
                                            :color="option.isLiked ? 'red' : 'grey lighten-1'"
                                            :disabled="!isLoggedIn"
                                        >
                                            <v-icon :small="$vuetify.breakpoint.smAndDown">
                                                {{ option.isLiked ? 'mdi-heart' : 'mdi-heart-outline' }}
                                            </v-icon>
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                            <div v-else class="empty-state">
                                <v-icon large color="grey lighten-1">mdi-comment-text-outline</v-icon>
                                <p class="text-muted">
                                    {{ isLoggedIn ? '暂无选项，快来添加第一条吧！' : '暂无选项，请先登录后添加' }}
                                </p>
                                <v-btn
                                    v-if="!isLoggedIn"
                                    color="primary"
                                    text
                                    small
                                    to="/login"
                                    class="mt-2"
                                >
                                    去登录
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
            {{ snackbarMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false" x-small>关闭</v-btn>
            </template>
        </v-snackbar>

        <!-- 添加选项对话框 -->
        <v-dialog v-model="dialog" max-width="500px">
            <v-card>
                <v-card-title class="text-h6">添加新选项</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="newOption"
                        label="输入选项文本"
                        placeholder="输入你要添加的选项..."
                        prepend-icon="mdi-comment-plus"
                        @keyup.enter="addOption"
                        :disabled="!isLoggedIn"
                        full-width
                        :rules="[v => v && v.length <= 100 || '选项长度不能超过100个字符']"
                        maxlength="100"
                        counter
                    ></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelDialog">取消</v-btn>
                    <v-btn color="primary" @click="addOption" :disabled="!isLoggedIn || !newOption.trim()">添加</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import service from '../utils/request.js';

export default {
    name: "Selection",
    data() {
        return {
            options: [],
            newOption: '',
            snackbar: false,
            snackbarMessage: '',
            snackbarColor: 'success',
            token: '',
            isLoggedIn: false,
            currentTopic: '',
            topicTitle: '评选活动',
            dialog: false,
            loading: false
        }
    },
    created() {
        this.token = localStorage.getItem('token');
        this.isLoggedIn = !!this.token;
    },
    watch: {
        '$route.params.topic': {
            handler(newTopic, oldTopic) {
                if (newTopic !== oldTopic) {
                    this.fetchData();
                }
            },
            immediate: true
        }
    },
    methods: {
        fetchData() {
            const topic = this.$route.params.topic || '';
            this.currentTopic = topic;
            this.topicTitle = '评选活动'; // 直接设置标题
            // 确保每次获取最新的token
            this.token = localStorage.getItem('token');
            this.isLoggedIn = !!this.token;
            
            if (!this.isLoggedIn) {
                this.showSnackbar('请先登录', 'warning');
            } else {
                this.fetchOptions();
            }
        },
        fetchOptions() {
            this.loading = true;
            const params = {
                topic: this.currentTopic,
                token: this.token || '' // 确保始终发送token参数，即使为空
            };

            service.get('/api/collect/list', { params }).then(response => {
                // 直接检查业务逻辑码，不依赖HTTP状态码
                if (response.data.code === 200) {
                    this.options = response.data.data;
                } else {
                    this.showSnackbar('获取选项列表失败', 'error');
                }
            }).catch(error => {
                console.error('获取选项列表失败:', error);
                this.showSnackbar('获取选项列表失败', 'error');
            }).finally(() => {
                this.loading = false;
            });
        },
        addOption() {
            if (!this.isLoggedIn) {
                this.showSnackbar('请先登录', 'warning');
                return;
            }

            const content = this.stripHtml(this.newOption.trim());
            if (!content) {
                this.showSnackbar('选项内容不能为空', 'warning');
                return;
            }

            this.loading = true;
            service.post('/api/collect/add', {
                topic: this.currentTopic,
                content: content,
                token: this.token
            }).then(response => {
                if (response.data.code === 200) {
                    this.newOption = '';
                    this.dialog = false; // 关闭对话框
                    this.fetchOptions();
                    this.showSnackbar('选项添加成功', 'success');
                } else {
                    this.showSnackbar('添加选项失败: ' + (response.data.message || '未知错误'), 'error');
                }
            }).catch(error => {
                console.error('添加选项失败:', error);
                this.showSnackbar('添加选项失败', 'error');
            }).finally(() => {
                this.loading = false;
            });
        },

        cancelDialog() {
            this.dialog = false;
            this.newOption = '';
        },
        toggleLike(itemId) {
            if (!this.isLoggedIn) {
                this.showSnackbar('请先登录', 'warning');
                return;
            }

            service.post('/api/collect/toggle-like', {
                itemId: itemId,
                topic: this.currentTopic,
                token: this.token
            }).then(response => {
                if (response.data.code === 200) {
                    const option = this.options.find(o => o.id === itemId);
                    if (option) {
                        option.isLiked = response.data.data.action === 'liked';
                        // 不更新总赞数，前端不需要知道
                    }
                    this.showSnackbar(
                        response.data.data.action === 'liked' ? '点赞成功' : '取消点赞成功',
                        'success'
                    );
                } else {
                    this.showSnackbar('操作失败: ' + (response.data.message || '未知错误'), 'error');
                }
            }).catch(error => {
                console.error('点赞操作失败:', error);
                this.showSnackbar('操作失败', 'error');
            });
        },
        // 过滤HTML标签，防止XSS攻击
        stripHtml(html) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            return tempDiv.textContent || tempDiv.innerText || '';
        },
        showSnackbar(message, color = 'success') {
            this.snackbarMessage = message;
            this.snackbarColor = color;
            this.snackbar = true;
        }
    }
};
</script>

<style scoped>
.selection-page {
    min-height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
}

.selection-container {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    background-color: #f5f5f5;
}

.option-list {
    max-height: calc(100vh - 300px);
    overflow-y: auto;
}

.option-item {
    cursor: pointer;
    transition: background-color 0.3s;
}

.option-item:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.option-content {
    font-size: 16px;
    line-height: 1.5;
}

.empty-state {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 40px 20px;
    color: #9e9e9e;
}

.empty-state v-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

/* 自定义滚动条样式 */
.option-list::-webkit-scrollbar {
    width: 8px;
}

.option-list::-webkit-scrollbar-track {
    background: #e0e0e0;
    border-radius: 4px;
}

.option-list::-webkit-scrollbar-thumb {
    background: #4CAF50;
    border-radius: 4px;
}

.option-list::-webkit-scrollbar-thumb:hover {
    background: #45a049;
}
</style>